import React from "react";
import Toaster from "../Toaster";
import EmailTemplate from "../../code/models/EmailTemplate";
import ApiEmailTemplates from "../../code/api/ApiEmailTemplates";
import CclDataTable, { CclDataTableColumnDef, CclDataTableColumnType } from "../CclDataTable";
import ButtonConfirm, { ButtonConfirmSize } from "../ButtonConfirm";
import MessageOptoutType from "../../code/models/MessageOptoutType";
import EmailEditor from "../emaileditor/EmailEditor";
import TemplateTag from "../../code/models/TemplateTag";
import "../../css/adminemailtemplates.css";

export default function AdminEmailTemplates() {
    const [toastMessage, setToastMessage] = React.useState<string>("");
    const [templates, setTemplates] = React.useState<EmailTemplate[]>([]);
    const [messageTypes, setMessageTypes] = React.useState<MessageOptoutType[]>([]);

    const [selectedTemplate, setSelectedTemplate] = React.useState<EmailTemplate | null>(null);
    const [editTemplate, setEditTemplate] = React.useState<EmailTemplate | null>(null);
    const [anyChanges, setAnyChanges] = React.useState<boolean>(false);
    const [templateTags, setTemplateTags] = React.useState<TemplateTag[]>([]);

    React.useEffect(() => {
        ApiEmailTemplates.getTemplateTags().then((tags) => {
            setTemplateTags(tags);
        });
        loadMessageOptoutTypes().then(() => {
            loadTemplates();
        });
    }, []);

    async function loadTemplates() {
        setSelectedTemplate(null);
        setEditTemplate(null);
        setAnyChanges(false);
        ApiEmailTemplates.getEmailTemplates().then((temps) => {
            setTemplates(temps);
        });
    }

    async function loadMessageOptoutTypes() {
        ApiEmailTemplates.getMessageOptoutTypes().then((results) => {
            setMessageTypes(results || []);
        });
    }

    function rowSelected(rowData: EmailTemplate): void {
        setSelectedTemplate(rowData);
        setEditTemplate(structuredClone(rowData));
        setAnyChanges(false);
    }

    function emailEditorTextChanged(newText: string) {
        if (editTemplate !== null) {
            editTemplate.template = newText;
        }

        checkHasChanges();
    }

    function checkHasChanges() {
        var isAnyChanges = editTemplate !== null &&
            selectedTemplate !== null &&
            (selectedTemplate.name !== editTemplate.name ||
                selectedTemplate.subject !== editTemplate.subject ||
                selectedTemplate.template !== editTemplate.template ||
                selectedTemplate.sendGridTemplateId !== editTemplate.sendGridTemplateId ||
                selectedTemplate.messageOptoutTypeId !== editTemplate.messageOptoutTypeId);
        setAnyChanges(isAnyChanges);
    }

    const bplColumnDefs: CclDataTableColumnDef[] = [
        {
            fieldName: "name",
            title: "Templates",
            visible: true,
            type: CclDataTableColumnType.Data
        }
    ];

    function addNewClick(): void {
        setSelectedTemplate(new EmailTemplate());
        setEditTemplate(new EmailTemplate());
        setAnyChanges(false);
    }

    function saveTemplateClick(): void {
        if (anyChanges && !!editTemplate && !!selectedTemplate) {
            ApiEmailTemplates.saveEmailTemplate(editTemplate).then((savedTempl) => {
                let isNew = editTemplate.emailTemplateId || 0 == 0;
                if (!!savedTempl) {
                    if (isNew) {
                        selectedTemplate.emailTemplateId = savedTempl.emailTemplateId;
                        selectedTemplate.companyId = savedTempl.companyId;
                    }
                    selectedTemplate.name = savedTempl.name;
                    selectedTemplate.subject = savedTempl.subject;
                    selectedTemplate.template = savedTempl.template;
                    selectedTemplate.sendGridTemplateId = savedTempl.sendGridTemplateId;
                    selectedTemplate.messageOptoutTypeId = savedTempl.messageOptoutTypeId;

                    setToastMessage(`Email temaplate "${editTemplate.name}" saved.`);

                    if (isNew) {
                        loadTemplates();
                    } else {
                        rowSelected(selectedTemplate);
                    }
                }
            });
        }
    }

    function deleteTemplateClick(confirm: boolean): void {
        if (!selectedTemplate || !confirm) return;
        if (selectedTemplate.emailTemplateId > 0) {
            ApiEmailTemplates.deleteEmailTemplate(selectedTemplate.emailTemplateId).then((success) => {
                if (success) {
                    setToastMessage(`Email template "${selectedTemplate.name}" was deleted.`);
                    loadTemplates();
                } else {
                    setToastMessage("Deletion of email template failed.");
                }
            });
        }
    }

    return (
        <React.Fragment>
            <div className="emmailtemplates-main">
                <div className="emailtemplates-left">
                    <div className="emailtemplates-button">
                        <button className="btn btn-sm btn-outline-primary" onClick={addNewClick} >Add Template</button>
                    </div>
                    <div className="emmailtemplates-table-wrapper">
                        <CclDataTable
                            name={"profilesTable"}
                            tableData={templates || []}
                            columnDefinitions={bplColumnDefs}
                            onRowSelect={rowSelected}
                            rowSelect={true}
                            onButtonClick={null} />
                    </div>
                </div>
                <div className="emailtemplates-right" key={"et-edit-" + (selectedTemplate?.emailTemplateId || "a")}>
                    <div className="row emailtemplates-editortop">
                        <div className="col-md-2">
                            <label htmlFor="templateName" className="form-label">Name</label>
                            <input className="form-control" disabled={!editTemplate}
                                id="templateName" defaultValue={selectedTemplate?.name} autoComplete={"off"}
                                onChange={(e) => {
                                    if (!!editTemplate) {
                                        editTemplate.name = e.target.value;
                                        checkHasChanges();
                                    }
                                }}></input>
                        </div>
                        <div className="col-md-2">
                            <label htmlFor="templateSubject" className="form-label">Subject</label>
                            <input className="form-control" disabled={!editTemplate}
                                id="templateSubject" defaultValue={selectedTemplate?.subject} autoComplete={"off"}
                                onChange={(e) => {
                                    if (!!editTemplate) {
                                        editTemplate.subject = e.target.value;
                                        checkHasChanges();
                                    }
                                }}></input>
                        </div>
                        <div className="col-md-2">
                            <label htmlFor="templateMessageType" className="form-label text-nowrap">Message Type</label>
                            <select className="form-select" disabled={!editTemplate} id="templateMessageType" defaultValue={`${selectedTemplate?.messageOptoutTypeId}`}
                                onChange={(e) => {
                                    if (!!editTemplate) {
                                        editTemplate.messageOptoutTypeId = parseInt(e.target.value);
                                        checkHasChanges();
                                    }
                                }}>
                                {messageTypes.map((mt) => {
                                    return (
                                        <option value={mt.id} key={`setmt-${mt.id}`}>{mt.name}</option>
                                    )
                                })}
                            </select>
                        </div>
                        <div className="col-md-1">
                            <label htmlFor="templateSendgridId" className="form-label text-nowrap">SendGrid Id</label>
                            <input className="form-control" disabled={!editTemplate}
                                id="templateSendgridId" defaultValue={selectedTemplate?.sendGridTemplateId} autoComplete={"off"}
                                onChange={(e) => {
                                    if (!!editTemplate) {
                                        editTemplate.sendGridTemplateId = e.target.value;
                                        checkHasChanges();
                                    }
                                }}></input>
                        </div>
                        <div className="col-md-1 emailtemplates-save text-nowrap">
                            <button className={"btn btn-sm " + (!editTemplate ? "btn-outline-secondary" : "btn-primary")}
                                disabled={!editTemplate || !anyChanges} onClick={saveTemplateClick} >Save Template</button>
                        </div>
                        <div className="col-md-2 emailtemplates-save text-nowrap">
                            {!!selectedTemplate && selectedTemplate.emailTemplateId > 0 &&
                                <ButtonConfirm cssClass="btn-outline-danger" confirmTitle="Delete Template"
                                    confirmMessage={`Delete email template "${selectedTemplate?.name}'?`}
                                    buttonText="Delete Template" onConfirm={deleteTemplateClick} enabled={true}
                                    buttonSize={ButtonConfirmSize.Small} />}
                        </div>
                    </div>
                    <div className="row emailtemplates-editormain">
                        {!!editTemplate && <EmailEditor
                            initialValue={editTemplate.template}
                            onChange={(newText) => emailEditorTextChanged(newText)}
                            templateTags={templateTags}
                            previewMode={false} />}
                    </div>
                </div>
            </div>
            <Toaster title="Email Templates" subtitle="Email" message={toastMessage} key={"emailtemplatetoast"} />
        </React.Fragment>
    )
}
